<template>
    <Card class="mt-3">
        <template v-slot:title>
            <div class="mb-5 flex items-center gap-2">
                <span class="text-base font-bold text-black">
                    {{cardTitle}}
                </span>
                <SimilarTag v-if="info?.similarity"
                            :label="$t('similarity')"
                            :similarity="info.similarity"
                />
            </div>
        </template>
        <p class="mb-4 overflow-hidden text-ellipsis whitespace-nowrap text-2xl font-bold leading-8 text-black" v-html="info.title">
        </p>
        <p class="mb-10 flex h-6 flex-row items-center">
            <span v-show="info.pn" class="inline-block h-6 rounded bg-grey-20 px-2 text-xs leading-6 text-black">{{ $t('pn', {pn: info.pn}) }}</span>
            <span class="ml-[50px] flex flex-row items-center">
                <img v-if="info.orgLogo" :src="info.orgLogo" alt="" class="mr-2 h-6 w-6 object-contain">
                <IconOrg v-else class="mr-2 h-6 w-6 rounded border border-grey-40 object-contain p-0.5"/>

                <span class="text-xs leading-6 text-black">{{info.org}}</span>
            </span>
        </p>
        <!-- 技术问题等 -->
        <div v-for="data in list" :key="data.title" class="p-3 even:bg-grey-20">
            <p class="mb-4 text-base font-bold leading-6 text-black" v-html="data.title"></p>
            <p class="line-clamp-6 text-sm leading-5 text-black" :title="data.description" v-html="data.description"></p>
            <p class="mt-4 w-full" :class="{'columns-2': data.images?.length > 1}">
                <img v-for="(img, idx) in data.images" :key="idx" :src="img" alt="" class="mx-auto mb-4 w-2/3">
            </p>
        </div>

        <!-- 特征对比 -->
        <p class="pb-4 pt-10 text-base font-bold leading-6 text-black">{{ $t('featureComparison') }}</p>
        <div class="h-fit w-full">
            <!-- header -->
            <div class="flex h-12 flex-row flex-nowrap items-center border-b border-b-grey-40 bg-blue-40 px-6 text-xs font-bold text-eureka-600">
                <div class="w-1/2">{{ $t('yourTechnicalPoints') }}</div>
                <div class="w-1/2">{{ $t('currentSimilarText') }}</div>
            </div>
            <!-- body -->
            <div
                v-for="(feature, idx) in featureList"
                :key="idx"
                class="flex flex-row flex-nowrap items-center px-6 py-5 text-xs text-black odd:bg-grey-20"
            >
                <div class="w-1/2 pr-10" v-html="feature.curr"></div>
                <div class="line-clamp-10 w-1/2" v-html="addWrap(feature.patent)"></div>
            </div>
        </div>

        <CompareGPTSummary v-if="showSummary" :patent_id="info.patent_id" class="mt-10"/>
    </Card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Card from '@/components/Card.vue';
import type { IComparingData, IFeatureData } from '@/types';
import { useI18n } from 'vue-i18n';
import IconOrg from '@/components/icons/IconOrg.vue'
import SimilarTag from '@/components/SimilarTag.vue'
import { splitText } from '@eureka/utils/text';
import CompareGPTSummary from '@/components/report/CompareGPTSummary.vue'
import { controlDeferred } from '@eureka/utils/control';

const { t: $t } = useI18n();

const props = defineProps<{
    no: number,
    info: {
        title: string,
        similarity: string,
        pn: string,
        org: string,
        orgLogo?: string,
        patent_id: string
    },
    list: IComparingData[],
    featureList: IFeatureData[]
}>();

const cardTitle = computed(() => $t('patentTitle', { no: props.no }));

function addWrap(text: string) {
    return splitText(text).map(p => `<p class="mb-1.5 last:mb-0">${p}</p>`).join('')
}

const showSummary = ref(false)

onMounted(async() => {
    /**
     * 是否是免费用户
     */
    const { freemium } = await controlDeferred.promise;
    showSummary.value = !freemium
})
</script>
